import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { Body2, Caption1 } from "@activecollab/components";
import classnames from "classnames";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import UserLink from "~/components/UserLink/UserLink";
import {
  clearCommentParentHasNewComments,
  retrySubmitComment,
  updateCommentParentLastVisitTimestamp,
} from "~/store/actions/actions.comments.jsx";
import { getCommentById } from "~/store/selectors/selector.comments.jsx";
import { getParentLastVisitTimestampSelector } from "~/store/selectors/selector.lastVisit";
import { getLoggedUserId } from "~/store/selectors/selector.users";
import { CommentBody } from "./CommentBody.jsx";
import { CommentOptions } from "./CommentOptions.jsx";
import LastEdited from "./LastEdited.jsx";
import NewCommentsSplitterTransition from "./NewCommentsSplitterTransition.jsx";
import Ago from "../component.ago.jsx";
import Reactions from "../Reactions/index.jsx";

const CommentListItem = ({ id, orderNumber, onEdit, onDelete }) => {
  const dispatch = useDispatch();
  const container = useRef(null);

  const loggedUserId = useSelector(getLoggedUserId);

  const comment = useSelector((state) => getCommentById(state, id));
  let commentExists = comment ? true : false;

  const lastVisitTimestamp = useSelector((state) =>
    getParentLastVisitTimestampSelector(state, comment?.parent_type, comment?.parent_id)
  );

  const isNew = useMemo(() => {
    if (comment) {
      return (
        typeof lastVisitTimestamp === `number` &&
        lastVisitTimestamp < comment.created_on &&
        comment.created_by_id !== loggedUserId
      );
    }
    return false;
  }, [comment, lastVisitTimestamp, loggedUserId]);

  const createdOn = comment.created_on;
  const createdById = comment.created_by_id || 0;

  const createdByName = comment.created_by_name;
  const createdByEmail = comment.created_by_email;
  const isPending = comment.isPending();
  const hasError = comment.hasError();
  const isRealTime = comment.is_real_time;

  const onRetry = useCallback(
    (id) => {
      dispatch(retrySubmitComment(id));
    },
    [dispatch]
  );

  const { t } = useTranslation();

  const setContainerOverflowToVisible = useCallback(() => {
    if (container.current) {
      container.current.style.overflow = `visible`;
      container.current.style.height = `auto`;
    }
  }, []);

  const setContainerHeightToScrollHeight = useCallback(() => {
    if (container.current) {
      container.current.style.height = container.current.scrollHeight + `px`;
    }
  }, []);

  useEffect(() => {
    if (isRealTime) {
      container.current = document.querySelector(`.comment.realtime`);
      if (container.current) {
        setContainerHeightToScrollHeight();
        container.current.addEventListener(`transitionend`, setContainerOverflowToVisible);
      }
    }
    return () => {
      if (container.current) {
        container.current.removeEventListener(`transitionend`, setContainerOverflowToVisible);
      }
    };
  }, [isRealTime, setContainerHeightToScrollHeight, setContainerOverflowToVisible]);

  const handleRetry = useCallback(() => {
    onRetry(id);
  }, [id, onRetry]);

  const handleCommentInViewport = useCallback(() => {
    dispatch(clearCommentParentHasNewComments(id));

    setTimeout(() => {
      dispatch(updateCommentParentLastVisitTimestamp(id, moment.utc().format(`X`)));
    }, 3000);
  }, [dispatch, id]);

  if (!commentExists) return null;

  return (
    <div
      className={classnames(`comment tw-relative`, {
        new: isNew,
        no_reactions: isPending,
        realtime: isRealTime,
      })}
    >
      <div
        className={classnames(`bubble tw-flex tw-gap-4`, {
          pending: isPending,
        })}
      >
        <UserLink
          id={createdById}
          name={createdByName}
          email={createdByEmail}
          hideName
          size={32}
          className="tw-self-start tw-shrink-0"
          context="comment - avatar on posted comment"
        />

        <div className="bubble_container tw-w-full tw-min-w-0">
          <div className="bubble_meta tw-flex tw-justify-between tw-h-6">
            <span className="bubble_meta_details">
              <UserLink
                renderAs={Body2}
                id={createdById}
                name={createdByName}
                email={createdByEmail}
                hideAvatar
                short
                className="tw-mr-1"
                nameProps={{ weight: "bold", color: "secondary" }}
                notInteractive
                context="comment list item"
              />
              {!isPending && (
                <Caption1 className="tw-text-theme-700 tw-inline-block">
                  <Ago
                    timestamp={createdOn}
                    className="bubble_date"
                    refresh
                  />
                </Caption1>
              )}
              {hasError && (
                <span className="fvl_inline_warning">
                  {t("Failed to post comment")}.
                  <a
                    href="#"
                    onClick={handleRetry}
                  >
                    {t("Retry")}
                  </a>
                </span>
              )}
              <span className="object_comment_counter">
                <Caption1 className="tw-text-theme-700 tw-inline-block"> – #{orderNumber}</Caption1>
              </span>
            </span>
            {!isPending && (
              <CommentOptions
                comment={comment}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            )}
          </div>
          <CommentBody
            id={id}
            className="tw-leading-6"
          />

          {!isPending ? (
            <>
              <LastEdited comment={comment} />
              <Reactions
                parentType="Comment"
                parentId={id}
              />
            </>
          ) : null}
        </div>
      </div>
      <NewCommentsSplitterTransition
        commentId={id}
        onEnterViewport={handleCommentInViewport}
      />
    </div>
  );
};

CommentListItem.propTypes = {
  id: PropTypes.any.isRequired,
  orderNumber: PropTypes.number.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

export default CommentListItem;
